import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useProfileCompleteness } from "@/hooks/useProfileSettings";
import { Service } from "@/types/dashboard";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

interface ProfileCompletenessCardProps {
  services: Service[];
}

const sectionToRoute: Record<string, string> = {
  personalInfo: "/dashboard/settings/general",
  businessInfo: "/dashboard/settings/business",
  businessImages: "/dashboard/settings/business",
  socialLinks: "/dashboard/settings/social",
  services: "/dashboard/services",
  availability: "/dashboard/settings/availability",
  notifications: "/dashboard/settings/notifications",
};

const sectionLabels: Record<string, string> = {
  personalInfo: "Personal Info",
  businessInfo: "Business Info",
  businessImages: "Business Images",
  socialLinks: "Social Links",
  services: "Services",
  availability: "Availability",
  notifications: "Notifications",
};

export const ProfileCompletenessCard = ({ services }: ProfileCompletenessCardProps) => {
  const completeness = useProfileCompleteness(services);
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  if (!completeness) return null;
  const { overallScore, sectionScores, missingFieldsBySection } = completeness;
  const percent = Math.round((overallScore / 100) * 100); // out of 100

  // Hide if above 80%
  if (percent > 80) return null;

  // Move handleNavigate here so it's available in both views
  const handleNavigate = (section: string) => {
    const route = sectionToRoute[section];
    if (route) {
      navigate(route, { state: { missingFields: missingFieldsBySection[section] } });
    }
  };

  if (!expanded) {
    return (
      <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="font-medium text-yellow-700">Profile {percent}% complete</span>
        </div>
        <button
          className="text-blue-600 underline text-sm"
          onClick={() => setExpanded(true)}
        >
          Expand
        </button>
      </div>
    );
  }

  // Expanded view (full card)
  return (
    <Card className="mb-6 p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">Profile Completeness</h2>
        <button
          className="text-blue-600 underline text-sm"
          onClick={() => setExpanded(false)}
        >
          Collapse
        </button>
      </div>
      <Progress value={percent} className="mb-4" />
      <div className="space-y-2">
        {Object.entries(sectionScores).map(([section, score]) => {
          const isComplete = (score >= 0.99 *  // 99% or more
            (section === "services" ? 25 : section === "personalInfo" ? 15 : section === "businessInfo" ? 20 : section === "businessImages" ? 15 : section === "socialLinks" ? 10 : section === "availability" ? 10 : 5));
          return (
            <div key={section} className="flex items-center gap-2">
              {isComplete ? (
                <span className="text-green-600">✅</span>
              ) : (
                <span className="text-yellow-600">⚠️</span>
              )}
              <span className="capitalize">{section.replace(/([A-Z])/g, ' $1')}</span>
              {!isComplete && (
                <button
                  className="ml-2 text-blue-600 underline text-sm"
                  onClick={() => handleNavigate(section)}
                >
                  Complete
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}; 